import cloneDeep from "lodash.clonedeep";
import shortid from "shortid";
import { AccountingUtils } from "../Accounting";
import { Utils } from "revlock-webutils";

function childProducts(products, parent, productsByParent) {
    if (!parent) return [];

    return productsByParent[parent.code] || [];
}

function getProduct(salesOrderItem) {
    return salesOrderItem.product;
}

function getCustomFieldsName(customFieldAttribute, quantity) {
    if (customFieldAttribute && quantity) {
        for (let customField of Object.values(customFieldAttribute)) {
            if (customField.displayName === quantity) {
                return customField.name;
            }
        }
    }
    return undefined;
}

function copyFields(source, dest, fields) {
    fields.forEach((field) => {
        if (source[field]) {
            dest[field] = source[field];
        }
    });
}

export const preprocess = (originalSalesOrder, context) => {
    let {
        referenceData: {
            products,
            productsByParent,
            orgConfig,
            configProvider,
            organization
        }
    } = context;

    const createSinglePoItem = configProvider.createSinglePoItem();
    const reasonCodeValues = configProvider.expenseReasonCode();
    const customFieldAttribute = configProvider.customFieldsConfig();
    const listPriceCalc = configProvider.useProduceListPriceConfig();
    const deferredByItem = configProvider.deferredByItem();
    const billingAdjustmentEnabled = configProvider.deferredRevenueAllocation();
    const setSalePriceForBundleComponents =
        configProvider.setSalePriceForBundleComponents();

    const { contractId } = originalSalesOrder;

    // when organization partner is chargebee and the current salesorder is
    // not a contract term then don't infer the order.
    // check REV-10440
    if (
        createSinglePoItem &&
        organization &&
        organization.partner == "chargebee" &&
        !contractId
    ) {
        return originalSalesOrder;
    }

    if (!originalSalesOrder) return;

    const salesOrder = cloneDeep(originalSalesOrder);

    let salesOrderItems = cloneDeep(salesOrder.salesOrderItem);
    const poProductsProcessed = new Set();
    const poProcessedSalesOrderItemIds = [];

    const baseSalesOrderItems = salesOrderItems.filter((item) => {
        const product = getProduct(item);
        if (product && product.entityType === "BASE") {
            return item;
        }
    });

    const poSalesOrderItems = salesOrderItems.filter((item) => {
        const product = getProduct(item);
        if (product && product.entityType === "PO") {
            return item;
        }
    });

    let newSalesOrderItems = [];

    for (let salesOrderItem of salesOrderItems) {
        const product = getProduct(salesOrderItem);

        //only process base products..
        const poProducts = childProducts(products, product, productsByParent);

        if (product && poProducts.length) {
            for (let poProduct of poProducts) {
                // Allow one entry per PO product
                if (
                    createSinglePoItem &&
                    poProductsProcessed.has(poProduct.id)
                ) {
                    continue;
                }

                const { quantity = 1 } = poProduct;

                // itemToPush may already exist becuase of preprocess done in prior upload
                // if NOT FOUND then only create new item to push.
                let poItem = poSalesOrderItems.find(
                    (item) =>
                        item.productId == poProduct.id &&
                        item.parentSalesOrderItemId == salesOrderItem.id
                );

                let itemToPush = poItem || {
                    id: shortid.generate(),
                    // multiple line items, so different external id.
                    externalId: createSinglePoItem
                        ? poProduct.code
                        : `${salesOrderItem.id}-${poProduct.code}`,
                    salesOrderId: originalSalesOrder.id,
                    productId: poProduct.id,
                    quantity: 1,
                    salePrice: 0.0,
                    listPrice: listPriceCalc
                        ? AccountingUtils.computeListPrice(
                              poProduct,
                              salesOrderItem.startDate || salesOrder.orderDate,
                              salesOrderItem.endDate
                          )
                        : 0.0,
                    product: poProduct,
                    // this is to identify the parent of the current po line item
                    parentSalesOrderItemId: salesOrderItem.id
                };

                if (salesOrder.isTerminated) {
                    itemToPush.endDate = salesOrderItem.endDate;
                    itemToPush.dereferencedEndDate =
                        salesOrderItem.dereferencedEndDate;
                    itemToPush.userSelectedEndDate =
                        salesOrderItem.userSelectedEndDate;
                }

                if (listPriceCalc) {
                    itemToPush.isListPriceProRated = true;
                }

                if (!createSinglePoItem) {
                    if (AccountingUtils.isCreditNote(salesOrderItem)) {
                        itemToPush.isCreditNote = true;
                    }
                    // Allocation links a credit note's PO children to the invoice through
                    // referenceLineItemId, so it is copied whatever the deferred-by-item setting.
                    copyFields(salesOrderItem, itemToPush, [
                        "referenceLineItemId"
                    ]);
                    if (deferredByItem && billingAdjustmentEnabled) {
                        copyFields(salesOrderItem, itemToPush, [
                            "invoiceNumber",
                            "referenceInvoiceNumber",
                            "subscriptionId"
                        ]);
                    }
                }

                // TODO: always do this even if you you are using exsitng itemToPush or you have new item, because
                // the salesOrderItem.attribue may have updates.
                if (reasonCodeValues.length) {
                    // po item should have all it's parent attributes too.
                    itemToPush["attributes"] = Object.assign(
                        {},
                        poProduct.attributes,
                        salesOrderItem.attributes
                    );
                } else {
                    itemToPush["attributes"] = poProduct.attributes;
                }

                if (quantity === "Accumulate Quantity") {
                    itemToPush["quantity"] = 0;
                    // credit note should not be accumulated in quantity
                    for (let baseSalesOrderItem of baseSalesOrderItems) {
                        // reference invoice number is not equal to invoice number that means it is credit-note
                        if (
                            baseSalesOrderItem.product.id == product.id &&
                            !AccountingUtils.isCreditNote(baseSalesOrderItem)
                        ) {
                            itemToPush["quantity"] +=
                                baseSalesOrderItem.quantity;
                        }
                    }
                } else if (Utils.isNumeric(quantity)) {
                    itemToPush["quantity"] = Utils.isNumeric(quantity)
                        ? parseInt(quantity)
                        : 1;
                } else {
                    let customFieldName = getCustomFieldsName(
                        customFieldAttribute,
                        quantity
                    );
                    if (customFieldName) {
                        let quantityFromCustomField =
                            salesOrder.orderAttributes &&
                            salesOrder.orderAttributes[customFieldName];

                        if (
                            !quantityFromCustomField &&
                            salesOrderItem.attributes &&
                            salesOrderItem.attributes[customFieldName]
                        ) {
                            quantityFromCustomField =
                                salesOrderItem.attributes[customFieldName];
                        }

                        if (
                            !quantityFromCustomField &&
                            poProduct.attributes &&
                            poProduct.attributes[customFieldName]
                        ) {
                            quantityFromCustomField =
                                poProduct.attributes[customFieldName];
                        }

                        itemToPush["quantity"] = Utils.isNumeric(
                            quantityFromCustomField
                        )
                            ? parseInt(quantityFromCustomField)
                            : 1;
                    } else {
                        itemToPush["quantity"] = 1;
                    }
                }

                if (
                    poProduct.isBundle &&
                    setSalePriceForBundleComponents &&
                    poProduct.priceAllocation
                ) {
                    // for Absolute Quantity, quantity should be multiplied by parent quantity
                    if (Utils.isNumeric(poProduct.quantity)) {
                        itemToPush.quantity *= salesOrderItem.quantity;
                    }
                    const extendedSalePrice =
                        salesOrderItem.salePrice * salesOrderItem.quantity;
                    const priceAllocationPercent =
                        poProduct.priceAllocation / 100;
                    itemToPush.salePrice =
                        (extendedSalePrice * priceAllocationPercent) /
                        itemToPush.quantity;
                }

                if (poItem) {
                    const index = salesOrder.salesOrderItem.findIndex(
                        (item) => item.id === itemToPush["id"]
                    );
                    salesOrder.salesOrderItem[index] = itemToPush;
                } else {
                    newSalesOrderItems.push(itemToPush);
                }

                poProductsProcessed.add(poProduct.id);
            }

            salesOrderItem.poProcessed = true;
            // this is to populate poProcessed attribute at original line item level
            poProcessedSalesOrderItemIds.push(salesOrderItem.id);
        }
    }

    salesOrder.salesOrderItem.push(...newSalesOrderItems);

    if (poProcessedSalesOrderItemIds.length) {
        salesOrder.salesOrderItem.forEach((item) => {
            if (poProcessedSalesOrderItemIds.includes(item.id)) {
                item.poProcessed = true;
                if (
                    item.product.isBundle &&
                    item.product.entityType === "BASE" &&
                    setSalePriceForBundleComponents
                ) {
                    item.originalSalePrice = item.salePrice;
                    item.salePrice = 0;
                }
            }
        });
    }

    // context.referenceData["productsByParent"] = productsByParent;

    return salesOrder;
};
